/**
 * TemplatePicker — пресеты графика платежей: 50/50, 30/30/40, Ежемесячно (N мес.), Произвольно (N траншей).
 * POST /deals/:dealId/payments/template (mode: 'replace'). Предупреждает, что заменяет текущий график.
 *
 * Маппинг на контракт BulkTemplateRequest:
 *   50/50, 30/30/40   → template=PERCENT, parts=[{percent, offsetDays}]
 *   Ежемесячно (N)     → template=EQUAL_MONTHLY, months=N
 *   Произвольно (N)    → template=PERCENT, N равных долей с интервалом offsetDays (округление остатка в последнюю долю)
 * startDate — дата первого транша; суммы сервер считает от бюджета сделки.
 */

import { useRef, useState } from 'react';
import type { BulkTemplateRequest, PercentPart } from '../../lib/types';
import { todayYmd } from './lib/dates';
import { Dialog } from './ui/Dialog';
import { Button } from './ui/Button';
import { cx } from './ui/cx';
import { IconGrid } from './ui/icons';
import { useApplyTemplate, type DealScope } from './api';
import { ApiError } from '../../lib/apiClient';

type PresetId = 'p50_50' | 'p30_30_40' | 'monthly' | 'custom';

interface Preset {
  id: PresetId;
  label: string;
  hint: string;
}

const PRESETS: Preset[] = [
  { id: 'p50_50', label: '50 / 50', hint: 'Два равных транша' },
  { id: 'p30_30_40', label: '30 / 30 / 40', hint: 'Три транша' },
  { id: 'monthly', label: 'Ежемесячно', hint: 'Равные доли по месяцам' },
  { id: 'custom', label: 'Произвольно N', hint: 'N равных траншей с интервалом' },
];

function makeIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `pw-tpl-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** N равных процентных долей (последняя добирает остаток до 100), с равномерным сдвигом по дням. */
function equalPercentParts(count: number, intervalDays: number): PercentPart[] {
  const parts: PercentPart[] = [];
  const base = Math.floor((100 / count) * 100) / 100; // 2 знака
  let acc = 0;
  for (let i = 0; i < count; i += 1) {
    const isLast = i === count - 1;
    const percent = isLast ? Math.round((100 - acc) * 100) / 100 : base;
    acc += base;
    parts.push({ percent, offsetDays: i * intervalDays });
  }
  return parts;
}

export interface TemplatePickerProps {
  scope: DealScope;
  /** Пресеты графика из настроек (если заданы) — можно расширить список; сейчас показываем канонические 4. */
}

export function TemplatePicker({ scope }: TemplatePickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState<PresetId>('p50_50');
  const [count, setCount] = useState(3);
  const [intervalDays, setIntervalDays] = useState(30);
  const [months, setMonths] = useState(3);
  const [startDate, setStartDate] = useState<string>(todayYmd());
  const [error, setError] = useState<string | null>(null);
  const idempotencyKey = useRef(makeIdempotencyKey());

  const apply = useApplyTemplate(scope);

  const buildBody = (): BulkTemplateRequest => {
    const base = { mode: 'replace' as const, startDate };
    switch (preset) {
      case 'p50_50':
        return {
          ...base,
          template: 'PERCENT',
          parts: [
            { percent: 50, offsetDays: 0 },
            { percent: 50, offsetDays: intervalDays },
          ],
        };
      case 'p30_30_40':
        return {
          ...base,
          template: 'PERCENT',
          parts: [
            { percent: 30, offsetDays: 0 },
            { percent: 30, offsetDays: intervalDays },
            { percent: 40, offsetDays: intervalDays * 2 },
          ],
        };
      case 'monthly':
        return { ...base, template: 'EQUAL_MONTHLY', months };
      case 'custom':
        return { ...base, template: 'PERCENT', parts: equalPercentParts(count, intervalDays) };
    }
  };

  const submit = (): void => {
    setError(null);
    apply.mutate(
      { body: buildBody(), idempotencyKey: idempotencyKey.current },
      {
        onSuccess: () => {
          idempotencyKey.current = makeIdempotencyKey();
          setOpen(false);
        },
        onError: (e) => {
          setError(e instanceof ApiError ? e.message || 'Не удалось применить шаблон.' : 'Не удалось применить шаблон.');
        },
      },
    );
  };

  return (
    <>
      <Button variant="default" size="sm" icon={<IconGrid size={15} />} onClick={() => setOpen(true)}>
        Шаблон графика
      </Button>

      {open ? (
        <Dialog
          title="Шаблон графика платежей"
          onClose={() => setOpen(false)}
          size="md"
          footer={
            <>
              <Button variant="ghost" onClick={() => setOpen(false)} disabled={apply.isPending}>
                Отмена
              </Button>
              <Button variant="primary" onClick={submit} disabled={apply.isPending}>
                {apply.isPending ? 'Применение…' : 'Применить шаблон'}
              </Button>
            </>
          }
        >
          <p className="pw-hint pw-hint--warn">
            Шаблон разобьёт бюджет сделки на транши и <strong>заменит текущий неоплаченный график</strong>. Уже
            оплаченные транши сохранятся.
          </p>

          <div className="pw-tpl-grid" role="group" aria-label="Выбор шаблона">
            {PRESETS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={cx('pw-tpl-card', preset === t.id && 'pw-tpl-card--active')}
                aria-pressed={preset === t.id}
                onClick={() => setPreset(t.id)}
              >
                <strong>{t.label}</strong>
                <span>{t.hint}</span>
              </button>
            ))}
          </div>

          <div className="pw-tpl-params">
            {preset === 'custom' ? (
              <label className="pw-field">
                <span className="pw-field__label">Число траншей</span>
                <input
                  type="number"
                  className="pw-input"
                  min={2}
                  max={60}
                  value={count}
                  onChange={(e) => setCount(Math.max(2, Math.min(60, Number(e.target.value) || 2)))}
                />
              </label>
            ) : null}

            {preset === 'monthly' ? (
              <label className="pw-field">
                <span className="pw-field__label">Число месяцев</span>
                <input
                  type="number"
                  className="pw-input"
                  min={2}
                  max={60}
                  value={months}
                  onChange={(e) => setMonths(Math.max(2, Math.min(60, Number(e.target.value) || 2)))}
                />
              </label>
            ) : null}

            {preset !== 'monthly' ? (
              <label className="pw-field">
                <span className="pw-field__label">Интервал, дней</span>
                <input
                  type="number"
                  className="pw-input"
                  min={1}
                  max={365}
                  value={intervalDays}
                  onChange={(e) => setIntervalDays(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
                />
              </label>
            ) : null}

            <label className="pw-field">
              <span className="pw-field__label">Дата первого транша</span>
              <input
                type="date"
                className="pw-input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value || todayYmd())}
              />
            </label>
          </div>

          {error ? (
            <p className="pw-field__err" role="alert">
              {error}
            </p>
          ) : null}
        </Dialog>
      ) : null}
    </>
  );
}
